import { ActivePromises } from '../../Utility/ActivePromises';
import { $ } from '../../../vendor/jquery';

/* global BSMessagePort */

/**
 * @typedef {external:BSMessagePort.MessagePortEvent} BsMessageEvent
 * @property {BsMessageObject} data
 */

/**
 * @typedef {BsMessageObjectSuccess|BsMessageObjectFailure} BsMessageObject
 */

/**
 * @typedef {Record<string, unknown>} BsMessageObjectSuccess
 * @property {'OK'} status
 * @property {string} type
 */

/**
 * @typedef {Object} BsMessageObjectFailure
 * @property {'ERROR'} status
 * @property {string} type
 * @property {string} message - Error message
 */

/**
 * @callback BSMessengerCallback
 * @param {BsMessageEvent} message
 */

/**
 * @classdesc BrightScriptMessenger singleton.
 * @class
 * @deprecated use functions found on
 * {@link Platform.BrightSignPlayer#_bsApi} instead.
 * @todo Remove so this doesn't need to be downloaded to non-BrightSign
 * players.
 */
function BrightScriptMessenger() {
    // Singleton code
    if (BrightScriptMessenger._instance) {
        return BrightScriptMessenger._instance;
    } else if (!(this instanceof BrightScriptMessenger)) {
        //if the function wasn't called as a constructor, call it as a
        //constructor and return the result
        return new BrightScriptMessenger();
    }
    BrightScriptMessenger._instance = this;

    // initialize

    this._activePromises = new ActivePromises('postbackId');

    this._listeners = [];

    if (typeof BSMessagePort !== 'undefined') {
        this._messagePort = new BSMessagePort();
    } else {
        // Silently fail by mimicking `BSMessagePort` when not running on a
        // BrightSign player
        this._messagePort = {
            PostBSMessage: function () {},
        };
    }

    this._messagePort.onbsmessage = this.bsMessageListener.bind(this);
}

/**
 * @type {ActivePromises}
 * @protected
 */
BrightScriptMessenger.prototype._activePromises = undefined;

/**
 * @type {Array.<{type: string, listener: BSMessengerCallback}>}
 * @protected
 */
BrightScriptMessenger.prototype._listeners = undefined;

/**
 * @type {BSMessagePort}
 * @protected
 */
BrightScriptMessenger.prototype._messagePort = undefined;

/**
 * @type {string}
 * @protected
 */
BrightScriptMessenger.prototype._postbackIdPrefix = 'POSTBACK_';

/**
 * Track Postback ID count to help keep IDs unique
 * @type {number}
 * @protected
 */
BrightScriptMessenger.prototype._postbackIdCount = -1;

/**
 *
 * @param {BSMessengerCallback} listener
 */
BrightScriptMessenger.prototype.addListener = function (listener) {
    this._listeners.push({ type: '_all', listener: listener });
};

/**
 *
 * @param {string} type
 * @param {BSMessengerCallback} listener
 */
BrightScriptMessenger.prototype.addBSMessageListener = function (type, listener) {
    this._listeners.push({ type: type, listener: listener });
};

/**
 * @param {BsMessageEvent} message
 */
BrightScriptMessenger.prototype.bsMessageListener = function (message) {
    var activeItem;

    // postback listener
    if (message.data.type && message.data.type.indexOf(this._postbackIdPrefix) === 0) {
        activeItem = this._activePromises.get({ postbackId: message.data.type });

        if (activeItem !== null) {
            if (message.data.status === 'OK') {
                activeItem.promise.resolve(message.data);
            } else {
                activeItem.promise.reject(new BrightScriptMessenger.Error(message));
            }
        }
    }

    // general listener
    this._listeners.forEach(function (listener) {
        if (listener.type === '_all') {
            listener.listener(message);
        } else if (listener.type == message.data.type) {
            listener.listener(message);
        }
    });
};

/**
 * Get the next Postback ID
 * @param {string} code
 * @returns {string}
 * @protected
 */
BrightScriptMessenger.prototype._nextPostbackId = function (code) {
    // increment id count while keeping it in the range of 0-999
    this._postbackIdCount = (this._postbackIdCount + 1) % 1000;
    return (
        this._postbackIdPrefix +
        Date.now() +
        '_' +
        String(this._postbackIdCount).padStart(3, '0') +
        '_' +
        code
    );
};

/**
 * @param {string} code
 * @param {Object} [data]
 * @throws {Error}
 * @throws {BrightScriptMessenger.Error}
 * @throws {ActivePromises.TimeoutError}
 * @return {PromiseLike<BsMessageObjectSuccess>}
 */
BrightScriptMessenger.prototype.postMessage = function (code, data) {
    var activeItem = {};

    activeItem.postbackId = this._nextPostbackId(code);
    activeItem.promise = $.Deferred();

    activeItem.message = data || {};
    activeItem.message.code = code;
    activeItem.message.postbackId = activeItem.postbackId;

    this._activePromises.add(activeItem);
    this._messagePort.PostBSMessage(activeItem.message);

    return activeItem.promise;
};

/**
 * Singleton instance
 * @type {?BrightScriptMessenger}
 * @protected
 */
BrightScriptMessenger._instance = undefined;

/**
 * Get singleton instance
 * @return {BrightScriptMessenger}
 */
BrightScriptMessenger.getInstance = function () {
    return BrightScriptMessenger._instance || new BrightScriptMessenger();
};

/**
 * Convert value to boolean (with smart conversion i.e. 'no' == false)
 * @param value
 */
BrightScriptMessenger.toBoolean = function (value) {
    if (typeof value !== 'boolean') {
        if (typeof value === 'string') {
            if (value.match(/true|yes|on|1/i)) {
                value = true;
            } else if (value.match(/false|no|off|0/i)) {
                value = false;
            } else {
                value = null;
            }
        } else {
            value = Boolean(value);
        }
    }

    return value;
};

/**
 * @class
 * @param {BsMessageEvent} message
 * @augments Error
 */
BrightScriptMessenger.Error = function (message) {
    this.name = 'BrightScriptMessenger.Error';
    this.message = message.data.message;
    this.stack = new Error().stack;
    this.data = message.data;
};
BrightScriptMessenger.Error.prototype = Object.create(Error.prototype);
BrightScriptMessenger.Error.prototype.constructor = BrightScriptMessenger.Error;
BrightScriptMessenger.Error.prototype.toJSON = function () {
    return {
        message: this.message,
        name: this.name,
        stack: this.stack,
        data: this.data,
    };
};

export { BrightScriptMessenger };
